import { z } from 'zod';
import type { FieldConfig } from '@/components/blocks/forms';
import type {
  AssertFieldKeysMatch,
  AssertSchemaKeyCoverage,
  SchemaFieldKeys,
} from '@/components/blocks/forms/schemaSync';
import {
  optionalDate,
  optionalNumber,
  optionalText,
  requiredMonthYear,
  requiredText,
} from '@/lib/formSchemas';
import { siteFieldTooltips } from './masterDataFieldTooltips';

const PERIODICITY_VALUES = ['1', '3', '4', '6'] as const;

const periodicityField = z
  .enum(PERIODICITY_VALUES)
  .or(z.literal(''))
  .transform((value) => (value === '' ? undefined : value))
  .refine((value) => value !== undefined, {
    message: 'Seleziona la periodicità',
  });

export const siteFormSchema = z
  .object({
    name: requiredText(2, 120, {
      min: 'Inserisci il nome dello stabilimento',
      max: 'Massimo 120 caratteri',
    }),
    sedeOperativaAddress: requiredText(4, 160, {
      min: "Inserisci l'indirizzo dello stabilimento",
      max: 'Massimo 160 caratteri',
    }),
    sedeOperativaCap: requiredText(4, 10, {
      min: 'Inserisci il CAP',
      max: 'Massimo 10 caratteri',
    }),
    sedeOperativaCity: requiredText(2, 80, {
      min: 'Inserisci il comune',
      max: 'Massimo 80 caratteri',
    }),
    sedeOperativaProvince: requiredText(2, 40, {
      min: 'Inserisci la provincia',
      max: 'Massimo 40 caratteri',
    }),
    periodicity: periodicityField,
    contractActivationDate: requiredMonthYear('Inserisci mese e anno di attivazione'),
    notes: optionalText(320),
    // CPI fields
    cpiPresent: z.boolean().optional(),
    cpiProtocolNumber: optionalText(40),
    cpiLastRenewalDate: optionalDate(),
    cpiExpiryDate: optionalDate(),
    ammoniaActivityCodes: z.boolean().optional(),
    // Authorization fields
    authorizationProtocolNumber: optionalText(40),
    authorizationIssueDate: optionalDate(),
    authorizationExpiryDate: optionalDate(),
    authorizedNh3Kg: optionalNumber(z.number().nonnegative()),
    officialGazettePublished: z.boolean().optional(),
    officialGazetteDate: optionalDate(),
    authorizationPrescriptions: optionalText(2000),
    // Contact fields
    reperibilePhone: optionalText(40),
  })
  .superRefine((values, ctx) => {
    if (values.cpiPresent) {
      if (!values.cpiLastRenewalDate) {
        ctx.addIssue({
          path: ['cpiLastRenewalDate'],
          code: z.ZodIssueCode.custom,
          message: "Inserisci la data dell'ultimo rinnovo CPI",
        });
      }
      if (!values.cpiExpiryDate) {
        ctx.addIssue({
          path: ['cpiExpiryDate'],
          code: z.ZodIssueCode.custom,
          message: 'Inserisci la data di scadenza CPI',
        });
      }
    }

    if (values.officialGazettePublished && !values.officialGazetteDate) {
      ctx.addIssue({
        path: ['officialGazetteDate'],
        code: z.ZodIssueCode.custom,
        message: 'Inserisci la data di pubblicazione G.U.',
      });
    }

    if (
      typeof values.name === 'string' &&
      values.name.trim().length > 0 &&
      (values.name.trim().toLowerCase() === 'impianto' ||
        /^impianto\s+\d+$/i.test(values.name.trim()))
    ) {
      ctx.addIssue({
        path: ['name'],
        code: z.ZodIssueCode.custom,
        message: 'Usa un nome di stabilimento, non un nome impianto.',
      });
    }
  });

export const siteFormFieldKeys = [
  'name',
  'sedeOperativaAddress',
  'sedeOperativaCap',
  'sedeOperativaCity',
  'sedeOperativaProvince',
  'periodicity',
  'contractActivationDate',
  'notes',
  'cpiPresent',
  'ammoniaActivityCodes',
  'cpiProtocolNumber',
  'cpiLastRenewalDate',
  'cpiExpiryDate',
  'authorizationProtocolNumber',
  'authorizationIssueDate',
  'authorizationExpiryDate',
  'authorizedNh3Kg',
  'officialGazettePublished',
  'officialGazetteDate',
  'authorizationPrescriptions',
  'reperibilePhone',
] as const satisfies SchemaFieldKeys<typeof siteFormSchema>;

const _siteFormSchemaCoverageCheck: AssertSchemaKeyCoverage<
  typeof siteFormSchema,
  typeof siteFormFieldKeys
> = true;
void _siteFormSchemaCoverageCheck;

/**
 * SINGLE SOURCE OF TRUTH for section configuration.
 * Both forms and anagrafica derive their sections from this.
 * Order determines rendering sequence everywhere.
 */
export const siteSections = {
  general: { title: 'Informazioni generali', order: 1 },
  cpi: { title: 'CPI', order: 2 },
  authorization: { title: 'Autorizzazione', order: 3 },
  notes: { title: 'Note', order: 4 }, // Form-only (notes field has displayFormatter: "skip")
} as const;

// Derive form section order from siteSections
const siteFormSectionOrder = Object.entries(siteSections)
  .sort(([, a], [, b]) => a.order - b.order)
  .map(([key]) => key);

// Derive form section titles from siteSections
const siteFormSectionTitles = Object.fromEntries(
  Object.entries(siteSections).map(([key, { title }]) => [key, title])
) as Record<string, string>;

// Generate form sections from field definitions
function generateFormSections(
  fields: readonly { readonly key: string; readonly section?: string }[],
  sectionOrder: string[],
  sectionTitles: Record<string, string>
) {
  const groups = new Map<string, string[]>();

  // Group fields by section
  for (const field of fields) {
    const section = field.section ?? 'general';
    const group = groups.get(section) ?? [];
    group.push(field.key);
    groups.set(section, group);
  }

  // Build sections in order
  return sectionOrder
    .filter((key) => groups.has(key))
    .map((key) => ({
      title: sectionTitles[key] ?? key,
      fields: groups.get(key)!,
    }));
}

export const siteFormFields = [
  {
    key: 'name',
    label: 'Nome',
    type: 'text',
    required: true,
    placeholder: 'Es: Stabilimento Bologna',
    section: 'general',
  },
  {
    key: 'sedeOperativaAddress',
    label: 'Indirizzo',
    type: 'text',
    required: true,
    placeholder: 'Es: Via Logistica 8',
    tooltip: siteFieldTooltips.address,
    colSpan: 2,
    section: 'general',
  },
  {
    key: 'sedeOperativaCap',
    label: 'CAP',
    type: 'text',
    required: true,
    placeholder: 'Es: 40100',
    colSpan: 2,
    section: 'general',
  },
  {
    key: 'sedeOperativaCity',
    label: 'Comune',
    type: 'text',
    required: true,
    placeholder: 'Es: Bologna',
    section: 'general',
  },
  {
    key: 'sedeOperativaProvince',
    label: 'Provincia',
    type: 'text',
    required: true,
    placeholder: 'Es: BO',
    section: 'general',
  },
  {
    key: 'periodicity',
    label: 'Periodicità',
    type: 'select',
    required: true,
    placeholder: 'Seleziona periodicità',
    tooltip: siteFieldTooltips.periodicity,
    options: [
      { value: '1', label: 'Mensile (1 mese)' },
      { value: '3', label: 'Trimestrale (3 mesi)' },
      { value: '4', label: 'Quadrimestrale (4 mesi)' },
      { value: '6', label: 'Semestrale (6 mesi)' },
    ],
    section: 'general',
    displayFormatter: 'select',
  },
  {
    key: 'contractActivationDate',
    label: 'Data attivazione contratto',
    type: 'month-year',
    required: true,
    placeholder: 'Es: 2026',
    tooltip: siteFieldTooltips.contractActivationDate,
    section: 'general',
    displayFormatter: 'month-year',
  },
  {
    key: 'cpiPresent',
    label: 'CPI presente',
    type: 'checkbox',
    tooltip: siteFieldTooltips.cpiPresent,
    section: 'cpi',
    displayFormatter: 'boolean',
  },
  {
    key: 'ammoniaActivityCodes',
    label: 'Codici attività ammoniaca',
    type: 'checkbox',
    tooltip: siteFieldTooltips.ammoniaActivityCodes,
    section: 'cpi',
    displayFormatter: 'boolean',
  },
  {
    key: 'cpiProtocolNumber',
    label: 'N. protocollo CPI',
    type: 'text',
    placeholder: 'Es: CPI-2025-112',
    tooltip: siteFieldTooltips.cpiProtocolNumber,
    description: 'Compila solo se CPI presente.',
    disabled: (values) => !values.cpiPresent,
    dependsOn: ['cpiPresent'],
    section: 'cpi',
  },
  {
    key: 'cpiLastRenewalDate',
    label: 'Rinnovo/Emissione CPI',
    type: 'date',
    placeholder: 'Es: 10/02/2025',
    tooltip: siteFieldTooltips.cpiLastRenewalDate,
    description: 'Compila solo se CPI presente.',
    disabled: (values) => !values.cpiPresent,
    dependsOn: ['cpiPresent'],
    section: 'cpi',
    displayFormatter: 'date',
  },
  {
    key: 'cpiExpiryDate',
    label: 'Scadenza CPI',
    type: 'date',
    placeholder: 'Es: 10/02/2027',
    tooltip: siteFieldTooltips.cpiExpiryDate,
    description: 'Compila solo se CPI presente.',
    disabled: (values) => !values.cpiPresent,
    dependsOn: ['cpiPresent'],
    section: 'cpi',
    displayFormatter: 'date',
  },
  {
    key: 'authorizationProtocolNumber',
    label: 'N. protocollo autorizzazione',
    type: 'text',
    placeholder: 'Es: 1234/2025',
    tooltip: siteFieldTooltips.authorizationProtocolNumber,
    section: 'authorization',
  },
  {
    key: 'authorizationIssueDate',
    label: 'Data rilascio',
    type: 'date',
    placeholder: 'Es: 07/03/2024',
    tooltip: siteFieldTooltips.authorizationIssueDate,
    section: 'authorization',
    displayFormatter: 'date',
  },
  {
    key: 'authorizationExpiryDate',
    label: 'Scadenza',
    type: 'date',
    placeholder: 'Es: 07/03/2029',
    tooltip: siteFieldTooltips.authorizationExpiryDate,
    section: 'authorization',
    displayFormatter: 'date',
  },
  {
    key: 'authorizedNh3Kg',
    label: 'NH₃ autorizzata (kg)',
    type: 'number',
    placeholder: 'Es: 2500',
    tooltip: siteFieldTooltips.authorizedNh3Kg,
    section: 'authorization',
    displayFormatter: 'number',
  },
  {
    key: 'officialGazettePublished',
    label: 'Pubblicazione su G.U.',
    type: 'checkbox',
    tooltip: siteFieldTooltips.officialGazettePublished,
    section: 'authorization',
    displayFormatter: 'boolean',
  },
  {
    key: 'officialGazetteDate',
    label: 'Data pubblicazione G.U.',
    type: 'date',
    placeholder: 'Es: 21/03/2024',
    tooltip: siteFieldTooltips.officialGazetteDate,
    description: 'Compila solo se la pubblicazione è presente.',
    disabled: (values) => !values.officialGazettePublished,
    dependsOn: ['officialGazettePublished'],
    section: 'authorization',
    displayFormatter: 'date',
  },
  {
    key: 'authorizationPrescriptions',
    label: 'Prescrizioni',
    type: 'textarea',
    placeholder: "Inserisci le prescrizioni dell'autorizzazione",
    tooltip: siteFieldTooltips.authorizationPrescriptions,
    section: 'authorization',
  },
  {
    key: 'reperibilePhone',
    label: 'Telefono reperibile',
    type: 'tel',
    placeholder: 'Es: +39 051 123456',
    tooltip: siteFieldTooltips.reperibilePhone,
    displayFormatter: 'skip', // Handled in manual contacts section
  },
  {
    key: 'notes',
    label: 'Note',
    type: 'textarea',
    placeholder: 'Note facoltative',
    tooltip: siteFieldTooltips.notes,
    section: 'notes',
    displayFormatter: 'skip', // Manual section with stacked layout
  },
] as const satisfies readonly FieldConfig[];

const _siteFormFieldKeyMatch: AssertFieldKeysMatch<
  typeof siteFormFieldKeys,
  typeof siteFormFields
> = siteFormFields;
void _siteFormFieldKeyMatch;

/**
 * Form sections generated from field definitions.
 * Single source of truth - derived from section property on each field.
 */
export const siteFormSections = generateFormSections(
  siteFormFields,
  siteFormSectionOrder,
  siteFormSectionTitles
);

/**
 * Flat field order derived from form sections.
 * Use this for useUnifiedForm's fieldOrder parameter.
 */
export const siteFieldOrder = siteFormSections.flatMap((s) => s.fields);

export type SiteFormValues = z.output<typeof siteFormSchema>;
